import React, { useEffect, useState, ChangeEvent } from "react";
import "./custom.css"; // Custom CSS for styling
import { fetchVersionRegistry } from "../services/azureBlobService";
import {
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  ClickAwayListener,
  InputBase,
  Button,
  SelectChangeEvent,
} from "@mui/material";
import { Box } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";

// --- Define interfaces/types ---
interface FileEntry {
  id: number;
  name: string;
  sid: string;
  description: string;
  version: string;
  database: string;
  type: string;
  blob_uri: string;
}

interface VersionEntry {
  id: number;
  name: string; // e.g. "V1.0.0"
  blob_uri: string; // e.g. "https://....EntityRegistryV1.0.0.json"
}

// --- 1) We define a mapping from Database -> Types for the Filter ---
const typesMapping: Record<string, string[]> = {
  Logistics: ["Packaging", "Transportation"],
  Materials: [
    "Elements",
    "Metals",
    "Composites",
    "Polymers",
    "Sinters",
    "Construction",
    "Chemicals",
  ],
  Production: ["Manufacturing", "Equipment", "Employees"],
  Electronics: [],
  Energy: [],
};

const DatabaseSearchPage: React.FC = () => {
  // Version-related states
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>("");

  // Registry data once version is chosen
  const [registry, setRegistry] = useState<FileEntry[]>([]);
  const [filteredData, setFilteredData] = useState<FileEntry[]>([]);

  // Loading state for the registry
  const [isLoadingRegistry, setIsLoadingRegistry] = useState<boolean>(false);

  // Filters
  const [databaseFilter, setDatabaseFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");

  // Pagination
  const [page, setPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);

  // UI state for search input focus
  const [inputFocus, setInputFocus] = useState(false);

  // 1) Load the Version Registry
  useEffect(() => {
    const loadVersionRegistry = async () => {
      try {
        const vData = await fetchVersionRegistry();
        setVersions(vData);
      } catch (err) {
        console.error("Error loading version registry:", err);
      }
    };
    loadVersionRegistry();
  }, []);
  const isMobile = useMediaQuery("(max-width: 768px)");
  // 2) When a version is picked, load that registry
  useEffect(() => {
    if (!selectedVersion) return;

    const loadRegistryForVersion = async () => {
      setIsLoadingRegistry(true);
      try {
        const verObj = versions.find((v) => v.name === selectedVersion);
        if (!verObj) return;

        const resp = await axios.get<FileEntry[]>(verObj.blob_uri);
        setRegistry(resp.data);
        setFilteredData(resp.data);
        setPage(0);
      } catch (err) {
        console.error("Error loading registry:", err);
      } finally {
        setIsLoadingRegistry(false);
      }
    };

    loadRegistryForVersion();
  }, [selectedVersion, versions]);

  // 3) Filter results
  const filterResults = () => {
    const filtered = registry.filter((file) => {
      return (
        (databaseFilter ? file.database === databaseFilter : true) &&
        (typeFilter ? file.type === typeFilter : true) &&
        (nameFilter
          ? file.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
            file.sid.toLowerCase().includes(nameFilter.toLowerCase())
          : true)
      );
    });
    setFilteredData(filtered);
    setPage(0);
  };

  // 4) Pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const currentItems = filteredData.slice(
    page * itemsPerPage,
    page * itemsPerPage + itemsPerPage,
  );

  // 5) Helper to open file
  const openInNewTab = (blobUri: string) => {
    window.open(blobUri, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center">
      {/* Title */}
      <div className="mt-10 w-full max-w-4xl mb-6 rounded-lg">
        <h1 className="text-4xl font-bold mb-4">Documentation Search</h1>
        <Typography variant="body1" className="text-left mt-5">
          The sustamize database stores +150,000 data sets with more than
          100,000 variants considering 246 electricity mixes of different
          countries and regions amounting to +2,000,000 reference CO2
          equivalents.
        </Typography>
      </div>

      {/* Filter Box (including Version, Database, Type) 
          We'll show it even if no version is chosen, 
          so user can choose version first. 
      */}
      <div className="mt-5 w-full max-w-4xl mb-6 rounded-lg shadow-lg bg-white">
        <Typography variant="h6" className="p-4">
          Filter Selection
        </Typography>
        <div className="p-6 flex flex-wrap gap-4 justify-center">
          {/* Version Filter (replace the old "yearFilter" approach) */}
          <Box className="xs:w-full sm:w-full min-[695px]:w-[14.375rem]">
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Version</InputLabel>
              <Select
                label="Version"
                value={selectedVersion}
                onChange={(e: SelectChangeEvent) => {
                  setSelectedVersion(e.target.value as string);
                  // Reset filters
                  setDatabaseFilter("");
                  setTypeFilter("");
                  setNameFilter("");
                  setFilteredData([]);
                  setRegistry([]);
                }}
              >
                <MenuItem value="">
                  <em>Version</em>
                </MenuItem>
                {versions.map((ver) => (
                  <MenuItem key={ver.id} value={ver.name}>
                    {ver.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Database Filter (only do anything once a version is selected) */}
          <Box className="xs:w-full sm:w-full min-[695px]:w-[14.375rem]">
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              disabled={!selectedVersion} // If no version, disable
            >
              <InputLabel>Database</InputLabel>
              <Select
                label="Database"
                value={databaseFilter}
                onChange={(e) => {
                  setDatabaseFilter(e.target.value);
                  setTypeFilter("");
                }}
              >
                <MenuItem value="">
                  <em>Database</em>
                </MenuItem>
                <MenuItem value="Energy">Energy</MenuItem>
                <MenuItem value="Logistics">Logistics</MenuItem>
                <MenuItem value="Materials">Materials</MenuItem>
                <MenuItem value="Electronics">Electronics</MenuItem>
                {/* Add other categories as needed */}
              </Select>
            </FormControl>
          </Box>

          {/* Type Filter */}
          {/* only show if there's a valid databaseFilter with non-empty type array */}
          {databaseFilter && typesMapping[databaseFilter]?.length > 0 && (
            <Box className="xs:w-full sm:w-full min-[695px]:w-[14.375rem]">
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Type</em>
                  </MenuItem>
                  {typesMapping[databaseFilter].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </div>

        {/* Apply Filters Button */}
        <div className="p-6 flex justify-end">
          <Button
            variant="outlined"
            onClick={filterResults}
            disabled={!selectedVersion}
          >
            Apply Filters
          </Button>
        </div>
      </div>

      {/* If no version is selected AND we aren't loading anything, show placeholder */}
      {!selectedVersion && !isLoadingRegistry && (
        <div className="flex flex-col items-center justify-center p-2 w-full">
          <img
            src="/images/no_results.svg"
            alt="No version selected"
            className="w-[125px] h-[125px] mb-3
                 xs:w-[60px] xs:h-[60px] 
                 sm:w-[80px] sm:h-[80px]"
          />
          <p
            className="text-xl font-semibold text-gray-700
                 xs:text-xs
                 sm:text-sm
                 text-center px-2"
          >
            No version selected. Please select a PFE version.
          </p>
        </div>
      )}

      {/* If we are loading the selected version's registry */}
      {isLoadingRegistry && selectedVersion && (
        <div className="mt-5 text-center">
          <p className="text-lg font-semibold">Loading registry...</p>
        </div>
      )}

      {/* Search Box & Table show only if we have a selectedVersion AND data is loaded */}
      {selectedVersion && !isLoadingRegistry && registry.length > 0 && (
        <>
          {/* Search Input */}
          <Paper
            className={`mt-2 mb-6 rounded-[10px] min-h-[50px] w-full max-w-4xl flex shadow-md`}
            elevation={2}
            onFocus={() => setInputFocus(true)}
          >
            {inputFocus ? (
              <div
                onClick={() => {
                  setInputFocus(false);
                  setNameFilter("");
                }}
                className="cursor-pointer flex items-center"
              >
                <ArrowBackIcon color="primary" className="ml-2 mr-1" />
              </div>
            ) : (
              <SearchIcon color="primary" className="ml-2 mr-1 self-center" />
            )}

            <ClickAwayListener
              onClickAway={() => {
                setInputFocus(false);
              }}
            >
              <InputBase
                placeholder="Search by Name or SID"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    filterResults();
                  }
                }}
                className="flex-auto border-0 px-2"
                inputProps={{
                  "aria-label": "search",
                }}
              />
            </ClickAwayListener>

            {inputFocus && (
              <div
                onClick={filterResults}
                className="cursor-pointer flex items-center"
              >
                <SearchIcon color="primary" className="ml-1 mr-2 self-center" />
              </div>
            )}
          </Paper>

          {/* Results Table */}
          <div className="w-full max-w-4xl">
            <Box>
              <Paper>
                {filteredData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center">
                    <img
                      src="/images/no_results.svg"
                      alt="No results found"
                      className="w-[125px] h-[125px]"
                    />
                    <p className="text-xl font-semibold text-gray-700">
                      No results found.
                    </p>
                  </div>
                ) : (
                  <>
                    <TablePagination
                      labelRowsPerPage="Rows per page:"
                      rowsPerPageOptions={[20, 50]}
                      component="div"
                      count={filteredData.length}
                      rowsPerPage={itemsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        // The toolbar wrapper
                        ".MuiTablePagination-toolbar": {
                          display: "flex",
                          alignItems: "center",
                          // If you want the pagination to spread out:
                          // justifyContent: "space-between",
                        },
                        // The "Rows per page" text
                        ".MuiTablePagination-selectLabel": {
                          margin: 0,
                          lineHeight: 1.5,
                        },
                        // The actual select element
                        ".MuiTablePagination-select": {
                          margin: "0 8px",
                          lineHeight: 1.5,
                        },
                        // The displayed rows text (e.g. "1–20 of 300")
                        ".MuiTablePagination-displayedRows": {
                          margin: 0,
                          lineHeight: 1.5,
                        },
                        // The pagination arrows container
                        ".MuiTablePagination-actions": {
                          marginLeft: "8px",
                        },
                      }}
                    />
                    {isMobile ? (
                      <div className="flex flex-col gap-3 p-3">
                        {currentItems.map((file) => (
                          <Paper
                            key={file.id}
                            onClick={() => openInNewTab(file.blob_uri)}
                            className="p-4 rounded-xl shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              {file.name}
                            </Typography>

                            {file.description &&
                              file.description.split(" | ").map((desc, idx) => (
                                <Typography
                                  key={idx}
                                  variant="body2"
                                  color="text.secondary"
                                  className="mt-1"
                                >
                                  {desc}
                                </Typography>
                              ))}

                            <Box className="mt-3 flex flex-wrap text-sm text-gray-600 gap-x-6 gap-y-1">
                              <div>
                                <strong>Database:</strong> {file.database}
                              </div>
                              <div>
                                <strong>Type:</strong> {file.type || "N/A"}
                              </div>
                              <div>
                                <strong>Version:</strong> {file.version}
                              </div>
                            </Box>
                          </Paper>
                        ))}
                      </div>
                    ) : (
                      <TableContainer className="table-responsive p-4 pl-7 w-full">
                        <Table
                          sx={{ minWidth: 0 }}
                          aria-labelledby="tableTitle"
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell className="p-4">Name</TableCell>
                              <TableCell className="p-4">Database</TableCell>
                              <TableCell className="p-4">Type</TableCell>
                              <TableCell className="p-4">Version</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentItems.map((file) => (
                              <TableRow
                                hover
                                key={file.id}
                                className="cursor-pointer"
                                onClick={() => openInNewTab(file.blob_uri)}
                              >
                                <TableCell
                                  className="p-4 w-[50%]"
                                  component="th"
                                  scope="row"
                                >
                                  <p className="font-bold">{file.name}</p>
                                  {file.description
                                    .split(" | ")
                                    .map((desc, idx) => (
                                      <p
                                        key={idx}
                                        className="text-sm text-gray-600"
                                      >
                                        {desc}
                                      </p>
                                    ))}
                                </TableCell>
                                <TableCell className="p-4 w-[15%]" align="left">
                                  {file.database}
                                </TableCell>
                                <TableCell className="p-4 w-[15%]" align="left">
                                  {file.type || "N/A"}
                                </TableCell>
                                <TableCell className="p-4 w-[20%]" align="left">
                                  {file.version}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {/* Pagination */}
                    <TablePagination
                      labelRowsPerPage="Rows per page:"
                      rowsPerPageOptions={[20, 50]}
                      component="div"
                      count={filteredData.length}
                      rowsPerPage={itemsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        // The toolbar wrapper
                        ".MuiTablePagination-toolbar": {
                          display: "flex",
                          alignItems: "center",
                          // If you want the pagination to spread out:
                          // justifyContent: "space-between",
                        },
                        // The "Rows per page" text
                        ".MuiTablePagination-selectLabel": {
                          margin: 0,
                          lineHeight: 1.5,
                        },
                        // The actual select element
                        ".MuiTablePagination-select": {
                          margin: "0 8px",
                          lineHeight: 1.5,
                        },
                        // The displayed rows text (e.g. "1–20 of 300")
                        ".MuiTablePagination-displayedRows": {
                          margin: 0,
                          lineHeight: 1.5,
                        },
                        // The pagination arrows container
                        ".MuiTablePagination-actions": {
                          marginLeft: "8px",
                        },
                      }}
                    />
                  </>
                )}
              </Paper>
            </Box>
          </div>
        </>
      )}
    </div>
  );
};

export default DatabaseSearchPage;
